<template>
    <PopupTemplate
        v-if="shouldShow"
        v-model="isOpen"
        :img-raw="imgSrc"
        :img-alt="t('vue.free_plan_intro_popup_img_alt')"
        :title="t('vue.free_plan_intro_popup_title')"
        @close="
            () => {
                dismissed = true;
                dismissForTtl();
            }
        "
    >
        <template #body>
            <div class="free-plan-intro-popup-description">
                <p
                    v-html="
                        t('vue.free_plan_intro_popup_description_primary_html')
                    "
                />
                <p
                    v-html="
                        t(
                            'vue.free_plan_intro_popup_description_secondary_html',
                        )
                    "
                />
            </div>
        </template>

        <template #actions>
            <AtomicButton
                color="yellow"
                size="lg"
                class="border-transparent bg-[#088BEA] text-white hover:text-gray-100 focus:ring-[#088BEA] disabled:bg-gray-300 disabled:text-white"
                @click="visitBilling"
            >
                {{ t("vue.free_plan_intro_popup_button") }}
            </AtomicButton>
        </template>
    </PopupTemplate>
</template>

<script setup lang="ts">
import imgSrc from "@/assets/images/free-plan-celebration.svg?raw";
import PopupTemplate from "@/components/Atomic/Dialogs/PopupTemplate.vue";
import AtomicButton from "@/components/Atomic/Buttons/AtomicButton.vue";
import { useTranslations } from "@/composables/useTranslations.ts";
import { router, usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import { computed, ref, watch } from "vue";

const { t } = useTranslations();
const page = usePage();

const allowedRoutesToShow = new Set([
    "home",
    "reports.index",
    "reports.show",
    "reports.show.source",
    "reports.show.ip",
    "alerts.overview",
    "alerts.settings.index",
    "blacklists.index",
]);
const currentRouteName = route().current();

const shouldShow = computed(() => {
    return (
        !page.props.hasFreePlan &&
        currentRouteName != null &&
        allowedRoutesToShow.has(currentRouteName)
    );
});

const isOpen = ref(false);
const dismissed = ref(false);

watch(
    shouldShow,
    (show) => {
        if (!show) {
            isOpen.value = false;
            return;
        }
        dismissed.value = isDismissed();
        isOpen.value = !dismissed.value;
    },
    { immediate: true },
);

watch(isOpen, (openNow) => {
    if (!openNow && shouldShow.value) {
        dismissed.value = true;
        dismissForTtl();
    }
});

function visitBilling() {
    router.visit(route("billing.subscriptions.index"));
    isOpen.value = false;
    dismissed.value = true;
    dismissForTtl();
}

const POPUP_KEY = "ui:free_plan_intro_popup_dismissed_until";
const TTL_MS = 1000 * 60 * 60;

function isDismissed(): boolean {
    const until = Number(localStorage.getItem(POPUP_KEY) ?? 0);
    return Number.isFinite(until) && until > Date.now();
}

function dismissForTtl() {
    localStorage.setItem(POPUP_KEY, String(Date.now() + TTL_MS));
}
</script>

<style scoped>
.free-plan-intro-popup-description {
    display: flex;
    flex-direction: column;
    gap: 18px;
    color: #3f3f46;
    text-align: center;
    font-family: Inter, sans-serif;
    font-size: 12.25px;
    font-style: normal;
    font-weight: 400;
    line-height: 142%;
}

:deep(.modal-body) {
    max-width: 433px !important;
    width: 100%;
}

.free-plan-intro-popup-description p {
    margin: 0;
}
</style>
